import * as React from "react";
import { ReactSortable } from "react-sortablejs";
import { Checkbox } from "./ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Edit, Info, EyeOff, Loader, ArrowUp, ArrowDown, Zap } from "lucide-react";
import { TbWindowMaximize, TbWindowMinimize } from "react-icons/tb";
import { ICompasAIChatAction, ICompassMessage } from "@/types/compass.types";
import { Button } from "./ui/button";
import { useRunCompassActions, useReorderActions } from "@/hooks/useCompassActions";
import { useDocument } from "@/hooks/useDocument";
import { useInvalidateCompassChat } from "@/hooks/useCompassChat";
import { useCancelQueuedAction } from "@/hooks/useCompassQueue";
import { toast } from "sonner";
const ActionItem = React.lazy(() => import("./ActionItem"));
const OneClickAutomationDialog = React.lazy(
  () => import("@/taskpane/components/OneClickAutomationDialog")
);

interface BulkActionButtonsProps {
  message: ICompassMessage;
  isExpanded: boolean;
  toggleExpandAll: () => void;
  onEditAll: () => void;
  onBulkActionToggle: (checked: boolean) => void;
  onOneClickAutomation: () => void;
  isAnyAction: boolean;
  isBulkActionChecked: boolean;
  isGlobalEditMode: boolean;
}

const BulkActionButtons: React.FC<BulkActionButtonsProps> = ({
  isExpanded,
  toggleExpandAll,
  onEditAll,
  onBulkActionToggle,
  onOneClickAutomation,
  isAnyAction,
  isBulkActionChecked,
  isGlobalEditMode,
  message,
}) => {
  const allExpanded = isExpanded;

  const bulkActions = [
    {
      id: "one-click-automation",
      type: "button",
      icon: (
        <div className="rounded-full animate-pulse-bolt">
          <Zap className="size-3.5 text-warning-200" />
        </div>
      ),
      tooltipContent: "Create 1-click-automation from actions",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onOneClickAutomation();
      },
    },
    {
      id: "toggle-expand-all",
      type: "button",
      icon: allExpanded ? (
        <TbWindowMinimize className="size-3.5" />
      ) : (
        <TbWindowMaximize className="size-3.5" />
      ),
      tooltipContent: allExpanded ? "Minimize all" : "Maximize all",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleExpandAll();
      },
    },
    {
      id: "edit-all-requests",
      type: "button",
      icon: isGlobalEditMode ? <EyeOff className="size-3.5" /> : <Edit className="size-3.5" />,
      tooltipContent: isGlobalEditMode ? "Exit edit mode" : "Edit all requests",
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onEditAll();
      },
    },
    {
      id: "select-bulk-actions",
      type: "checkbox",
      checked: isBulkActionChecked,
      onCheckedChange: onBulkActionToggle,
      tooltipContent: isBulkActionChecked
        ? "Cancel all pending actions"
        : "Select all and run bulk actions",
      disabled: !isAnyAction,
    },
  ].filter((action) => {
    if (action.id === "one-click-automation") {
      return (message?.actions?.length || 0) > 1;
    }
    return true;
  });

  return (
    <>
      {bulkActions.map((action) => (
        <TooltipProvider key={action.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              {action.type === "button" ? (
                <span onClick={action.onClick} className="cursor-pointer">
                  {action.icon}
                </span>
              ) : (
                <Checkbox
                  checked={action.checked}
                  onCheckedChange={action.onCheckedChange}
                  className="scale-90"
                  disabled={action.disabled}
                />
              )}
            </TooltipTrigger>
            <TooltipContent>
              <p>{action.tooltipContent}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </>
  );
};
interface ActionItemsProps {
  message: ICompassMessage;
  messages: ICompassMessage[];
}

const ActionItems: React.FC<ActionItemsProps> = ({ message }) => {
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());
  const [, setEditAllEvent] = React.useState(0);
  const [isBulkActionChecked, setIsBulkActionChecked] = React.useState(false);
  const [isGlobalEditMode, setIsGlobalEditMode] = React.useState(false);
  const [isOneClickDialogOpen, setIsOneClickDialogOpen] = React.useState(false);
  const [actions, setActions] = React.useState(message.actions || []);

  const { data: doc } = useDocument();
  const runActionsMutation = useRunCompassActions();
  const cancelActionsMutation = useCancelQueuedAction();
  const invalidateCompassChat = useInvalidateCompassChat();
  const reorderActionsMutation = useReorderActions();

  React.useEffect(() => {
    setActions(message.actions || []);
  }, [message.actions]);

  const handleReorder = (reorderedActions: ICompasAIChatAction[]) => {
    try {
      if (!Array.isArray(reorderedActions)) {
        console.warn("handleReorder received non-array:", reorderedActions);
        return;
      }

      // Check if the order has actually changed
      const currentActionIds = actions.map((a) => a.id).join(",");
      const newActionIds = reorderedActions.map((a) => a.id).join(",");

      if (currentActionIds === newActionIds) {
        setActions(reorderedActions);
        return;
      }

      setActions(reorderedActions);

      if (!doc?._id || !message.id) return;

      const actionIds = reorderedActions.map((action) => action.id);
      reorderActionsMutation.mutate(
        {
          message_id: message.id,
          action_ids: actionIds,
          context_id: doc._id,
        },
        {
          onSuccess: () => {
            if (doc?._id) {
              invalidateCompassChat(doc._id);
            }
          },
          onError: (error) => {
            console.error("Failed to reorder actions:", error);
            // Revert to original order on error
            setActions(message.actions || []);
          },
        }
      );
    } catch (error) {
      console.error("Error in handleReorder:", error);
    }
  };

  const moveAction = (fromIndex: number, toIndex: number) => {
    const reorderedActions = [...actions];
    const [item] = reorderedActions.splice(fromIndex, 1);
    if (!item) return;
    reorderedActions.splice(toIndex, 0, item);
    handleReorder(reorderedActions);
  };

  const getActionState = (action?: ICompasAIChatAction): string => {
    if (action?.status) {
      if (action.status === "processing") return "processing";
      if (action.status === "queued") return "queued";
      if (action.status === "pending") return "pending";
      if (action.status === "completed") return "processed";
      if (action.status === "failed") return "failed";
    }
    return "normal";
  };

  const handleRunAllActions = () => {
    if (!doc?._id || !doc.title || !actions || actions.length === 0) return;

    const actionRequests = actions.map((action) => ({
      id: action.id,
      prompt: action.prompt,
      module: action.module || "",
      feature: action.feature || "",
      current_document_id: doc._id,
      document_ids: [doc._id],
      document_title: [doc.title],
    }));

    runActionsMutation.mutate(
      {
        requests: actionRequests,
        documentId: doc._id,
      },
      {
        onSuccess: () => {
          if (doc?._id) {
            invalidateCompassChat(doc._id);
            toast.success("Compass started processing bulk actions", { duration: 2000 });
          }
        },
        onError: (error) => {
          toast.error("Failed to execute bulk actions", { duration: 2000 });
          console.error("Failed to execute bulk actions:", error);
          setIsBulkActionChecked(false);
        },
      }
    );
    setIsBulkActionChecked(true);
  };

  const handleCancelAllPendingActions = () => {
    const queuedActions = actions?.filter(
      (action) => getActionState(action) === "queued" && action.queue_item_id
    );

    if (!doc?._id || !queuedActions || queuedActions.length === 0) {
      setIsBulkActionChecked(false);
      return;
    }

    const jobIds = queuedActions.map((action) => action.queue_item_id!);

    cancelActionsMutation.mutate(
      {
        jobIds: jobIds,
        documentId: doc._id,
      },
      {
        onSuccess: () => {
          if (doc?._id) {
            invalidateCompassChat(doc._id);
          }
        },
        onError: (error) => {
          console.error("Failed to cancel bulk actions:", error);
          setIsBulkActionChecked(true);
        },
      }
    );
    setIsBulkActionChecked(false);
  };

  const handleBulkActionToggle = (checked: boolean) => {
    if (checked) {
      handleRunAllActions();
    } else {
      handleCancelAllPendingActions();
    }
  };

  const toggleExpansion = (actionId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(actionId)) {
        newSet.delete(actionId);
      } else {
        newSet.add(actionId);
      }
      return newSet;
    });
  };

  const toggleExpandAll = () => {
    if (expandedItems.size === actions.length) {
      setExpandedItems(new Set());
    } else {
      setExpandedItems(new Set(actions.map((a) => a.id)));
    }
  };

  const handleEditAll = () => {
    setIsGlobalEditMode((prev) => !prev);
    setEditAllEvent(Date.now()); // Still trigger this to propagate to individual ActionItems
  };

  return (
    <div className="w-full mt-2">
      <div className="flex justify-between items-center mb-3">
        <h5 className="flex items-center gap-2 font-medium">
          Action Items
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-3 h-3 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Actions that might interest you</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h5>
        <div className="flex items-center gap-2">
          <BulkActionButtons
            message={message}
            isExpanded={expandedItems.size === actions.length && actions.length > 0}
            toggleExpandAll={toggleExpandAll}
            onEditAll={handleEditAll}
            onBulkActionToggle={handleBulkActionToggle}
            onOneClickAutomation={() => setIsOneClickDialogOpen(true)}
            isAnyAction={!!(actions && actions.length > 0)}
            isBulkActionChecked={isBulkActionChecked}
            isGlobalEditMode={isGlobalEditMode}
          />
        </div>
      </div>
      <React.Suspense fallback={null}>
        {isOneClickDialogOpen && doc?._id && (
          <OneClickAutomationDialog
            isOpen={isOneClickDialogOpen}
            onClose={() => setIsOneClickDialogOpen(false)}
            actions={actions}
            documentId={doc._id}
          />
        )}
      </React.Suspense>
      <React.Suspense
        fallback={
          <div className="flex flex-col items-center justify-center h-full gap-4">
            <Loader className="animate-spin" size={48} />
            <p>Loading...</p>
          </div>
        }
      >
        <ReactSortable list={actions} setList={handleReorder} className="flex flex-col gap-2">
          {actions?.map((action, index) => {
            const actionState = getActionState(action);

            return (
              <div key={action.id} className="flex items-center gap-1.5">
                <div className="flex-1">
                  <ActionItem
                    action={action}
                    isExpanded={expandedItems.has(action.id)}
                    toggleExpansion={() => toggleExpansion(action.id)}
                    actionState={actionState}
                    isGlobalEditMode={isGlobalEditMode}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  {index > 0 && (
                    <Button
                      variant="outline"
                      size="icon-sm"
                      onClick={() => moveAction(index, index - 1)}
                      className="rounded-full size-6"
                    >
                      <ArrowUp className="size-3" />
                    </Button>
                  )}
                  {index < actions.length - 1 && (
                    <Button
                      variant="outline"
                      size="icon-sm"
                      onClick={() => moveAction(index, index + 1)}
                      className="rounded-full size-6"
                    >
                      <ArrowDown className="size-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </ReactSortable>
      </React.Suspense>
    </div>
  );
};

export default ActionItems;
